import { tool } from "ai"
import { z } from "zod"
import { cloneDeep } from "lodash"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { agents } from "@/lib/agents"
import { supabase } from "@/lib/agents/db"
import { DEFAULT_BUILTIN_FLAGS, flagsOf } from "@/lib/agents/escalationFlags"
import { useAssistant, useAssistantModule } from "@/components/assistant/context"
import type { ModuleCatalogEntry, ToolPolicy } from "@/lib/assistant/types"
import { makeAgentBuilderTools, AGENT_BUILDER_TOOL_LABELS, checkModelParameters } from "./tools"
import { buildAgentBuilderPrompt, FLOW_EDITOR_NOTES } from "./prompt"
import {
  makeAgentSettingsTools, AGENT_SETTINGS_TOOL_LABELS,
  checkFlagParameter, checkSetFlag, checkRemoveFlag, checkBindForm,
} from "./tools"

// The agent editor at /agent/<id>: building, configuring, test-running and saving the agent on the canvas

export const flowEditorCatalog: ModuleCatalogEntry = {
  id: "flow.editor",
  title: "Agent flow editor",
  route: "/agent/<agent id>",
  description: "Build and edit agents: workers, connections, parameters, escalation flags and the bound Magic Form. Open /agent/<id> for an existing agent (ids from list_agents), or /agent/new to create one, then save it with ab_save_agent.",
  resource: "agents",
}

export interface FlowEditorAssistantProps {
  agent: Agent | null
  // Called after every graph mutation so the canvas re-renders
  onGraphChanged: () => void
  onAgentReplaced: (next: Agent) => void
  onWorkerRemoved: (workerId: string) => void
}

// Workers that reach real people or outside systems; the assistant never test-runs an agent that has one
const SENDS_EXTERNALLY = new Set(["message"])

const read: ToolPolicy = { kind: "read", permission: ["agents", "read"] }
const edit: ToolPolicy = { kind: "write", permission: ["agents", "update"] }
const remove: ToolPolicy = { kind: "destructive", permission: ["agents", "update"] }
const POLICIES: Record<string, ToolPolicy> = {
  ab_list_worker_types: read,
  ab_describe_worker_type: read,
  ab_get_canvas: read,
  ab_list_agents: read,
  ab_get_agent: read,
  ab_copy_agent: edit,
  ab_add_worker: edit,
  ab_connect: edit,
  ab_set_parameters: edit,
  ab_set_field_value: edit,
  ab_add_condition: edit,
  ab_add_schema_field: edit,
  ab_reset_conversation: { kind: "write", permission: ["agents", "read"], selfReviewed: true },
  ab_run_agent: { kind: "write", permission: ["agents", "read"] },
  ab_remove_schema_field: remove,
  ab_disconnect: remove,
  ab_remove_worker: remove,
  ab_get_agent_settings: read,
  ab_set_flag: edit,
  ab_remove_flag: remove,
  ab_bind_form: edit,
  // Checks create/update itself and always asks unless auto-accept is on
  ab_save_agent: { kind: "write", permission: ["agents", "read"], selfReviewed: true },
}

// Registers the module while the editor is open. Renders nothing: the assistant panel is the UI
export function FlowEditorAssistant({ agent, onGraphChanged, onAgentReplaced, onWorkerRemoved }: FlowEditorAssistantProps) {
  const { selectedTeam } = useTeamStore()
  const { canCreate, canUpdate } = usePermissions()
  const { requestApproval, reloadWhenDone } = useAssistant()

  useAssistantModule("flow.editor", () => {
    if (!agent) return null
    let current = agent
    const tools: Record<string, any> = makeAgentBuilderTools({
      getAgent: () => current,
      refresh: onGraphChanged,
      // Later tool calls in the same step must see the replacement
      replaceAgent: next => { current = next; onAgentReplaced(next) },
      removeWorker: onWorkerRemoved,
      teamId: selectedTeam?.id,
    })
    Object.assign(tools, makeAgentSettingsTools({ getAgent: () => current, teamId: selectedTeam?.id }))

    const run = tools.ab_run_agent
    if (run?.execute) {
      tools.ab_run_agent = {
        ...run,
        execute: async (args: any, opts: any) => {
          const senders = Object.values<any>(current.workers ?? {}).filter(w => SENDS_EXTERNALLY.has(w.config?.type))
          if (senders.length) {
            return {
              ran: false,
              error: "This agent has a Send Message worker, so a test run could text a real contact. The assistant doesn't run it: ask the user to test it from the editor's chat.",
            }
          }
          return run.execute(args, opts)
        },
      }
    }

    // Same steps as the editor's Save button (components/flow/menu.tsx onSave)
    tools.ab_save_agent = tool({
      description: "Save the agent on the canvas to the database. Nothing you build is kept until this runs. A new agent gets its id here.",
      parameters: z.object({}),
      execute: async () => {
        const isNew = !current.id
        if (isNew ? !canCreate("agents") : !canUpdate("agents")) {
          return { error: `Your role can't ${isNew ? "create" : "update"} agents.` }
        }
        const ok = await requestApproval({
          summary: `${isNew ? "Create" : "Save"} agent "${current.title || "Untitled"}"`,
          detail: `${Object.keys(current.workers ?? {}).length} workers`,
        })
        if (!ok) return { error: "Rejected by reviewer." }
        if (!current.escalationFlags?.length) current.escalationFlags = DEFAULT_BUILTIN_FLAGS
        const { data: { user } } = await supabase.auth.getUser()
        const saved = await agents.saveAgent(cloneDeep(current), selectedTeam?.id, user?.id)
        current.id = saved.id
        // A new agent only knows its team once it's loaded from the database, so the page reloads after this reply
        if (isNew) {
          reloadWhenDone(`/agent/${saved.id}`)
          return { saved: true, id: saved.id, created: true, note: "The page reloads with the saved agent when this reply ends. Test runs need that reload, so finish your reply instead of testing now." }
        }
        return { saved: true, id: saved.id, created: false }
      },
    })

    const workers = Object.values<any>(current.workers ?? {})
    return {
      id: "flow.editor",
      title: "Agent flow editor",
      tools,
      policies: {
        ...POLICIES,
        ab_set_parameters: {
          ...POLICIES.ab_set_parameters,
          precheck: ({ workerId, parametersJson }: { workerId: string; parametersJson: string }) => {
            let parsed: any
            try { parsed = JSON.parse(parametersJson) } catch { return null }
            const type = current.workers?.[workerId]?.config?.type
            if (!parsed || typeof parsed !== "object") return null
            return checkModelParameters(type, parsed) ?? checkFlagParameter(current, type, parsed)
          },
        },
        ab_set_flag: { ...POLICIES.ab_set_flag, precheck: (a: any) => checkSetFlag(current, a) },
        ab_remove_flag: { ...POLICIES.ab_remove_flag, precheck: (a: any) => checkRemoveFlag(current, a.id) },
        ab_bind_form: { ...POLICIES.ab_bind_form, precheck: (a: any) => checkBindForm(selectedTeam?.id, a.formId) },
      },
      labels: { ...AGENT_BUILDER_TOOL_LABELS, ...AGENT_SETTINGS_TOOL_LABELS, ab_save_agent: "Save agent" },
      instructions: buildAgentBuilderPrompt({
        agentTitle: current.title,
        workerCount: workers.length,
        hasCmsSearch: workers.some(w => w.config?.type === "cmssearch"),
      }) + FLOW_EDITOR_NOTES,
      context: `Open agent: ${current.title || "Untitled"} (${current.id ? `id ${current.id}` : "new, not saved yet"}), ${workers.length} workers, `
        + `${flagsOf(current).filter(f => f.enabled).length} flags on, ${current.forms?.length ? `form ${current.forms[0]} bound` : "no form bound"}.`,
    }
  })

  return null
}

// Every tool the module can offer, for the strict-schema check
export function flowEditorToolsForCheck(ctx: any) {
  return { ...makeAgentBuilderTools(ctx), ...makeAgentSettingsTools(ctx) }
}
